import { Injectable, OnDestroy } from '@angular/core';
import { SessionService } from './session.service';
import { environment } from '@environments/environment';
import { UserService } from './user.service';
import { UserStatus } from '@models/user';
import { AppRealtimeService } from './app-realtime.service';

/**
 * Presence: uses the shared dashboard WebSocket for `presence:set` and application-level
 * ping (replaces periodic REST PATCH). Falls back to keepalive fetch when disconnecting offline.
 */
@Injectable({
  providedIn: 'root',
})
export class PresenceService implements OnDestroy {
  private isStarted = false;

  private readonly visibilityHandler = () => {
    if (document.visibilityState === 'visible') {
      // this.sendPresence(UserStatus.ACTIVE);
    }
  };

  private readonly onlineHandler = () => {
    // this.sendPresence(UserStatus.ACTIVE);
  };

  private readonly pageHideHandler = () => {
    this.appRealtime.stopPing();
  };

  constructor(
    private readonly userService: UserService,
    private readonly sessionService: SessionService,
    private readonly appRealtime: AppRealtimeService,
  ) {}

  start(): void {
    if (this.isStarted) return;
    this.isStarted = true;

    this.appRealtime.ensureConnected();
    this.appRealtime.startVisiblePingLoop();

    document.addEventListener('visibilitychange', this.visibilityHandler);
    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('pagehide', this.pageHideHandler);
    window.addEventListener('beforeunload', this.pageHideHandler);

    if (document.visibilityState === 'visible' && navigator.onLine) {
      // this.sendPresence(UserStatus.ACTIVE);
    }
  }

  stop(): void {
    this.appRealtime.stopPing();
    this.sendOfflineWithKeepAlive();
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    window.removeEventListener('online', this.onlineHandler);
    window.removeEventListener('pagehide', this.pageHideHandler);
    window.removeEventListener('beforeunload', this.pageHideHandler);
    this.isStarted = false;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private sendPresence(status: UserStatus): void {
    const user = this.sessionService.getUserValue();
    if (!user?.accessToken) return;

    if (this.appRealtime.isOpen()) {
      this.appRealtime.setPresenceStatus(status);
      return;
    }

    this.userService.updatePresence(status).subscribe({ error: () => {} });
  }

  private sendOfflineWithKeepAlive(): void {
    const user = this.sessionService.getUserValue();
    if (!user?.accessToken) return;

    if (this.appRealtime.isOpen()) {
      this.appRealtime.setPresenceStatus(UserStatus.OFFLINE);
    }

    const url = `${environment.apiUrl}/user/presence`;
    const body = JSON.stringify({ status: 'offline' });

    fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${user.accessToken}`,
        'Content-Type': 'application/json',
      },
      body,
      keepalive: true,
    }).catch(() => {});
  }
}
